import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api.js";
import { SERVICE_IDS, LANGS } from "../constants.js";

interface Props {
  mode: "create" | "edit";
}

function toLocalDatetime(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}

export function PushForm({ mode }: Props) {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [serviceId, setServiceId] = useState("nurie");
  const [lang, setLang] = useState<string>("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [announcementId, setAnnouncementId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;
    void (async () => {
      try {
        const list = await api.listPushMessages({ status: "all", limit: 200 });
        const found = list.items.find((m) => m.id === id);
        if (!found) {
          setError("見つかりませんでした");
          return;
        }
        setServiceId(found.service_id);
        setLang(found.lang ?? "");
        setTitle(found.title);
        setBody(found.body);
        setAnnouncementId(found.announcement_id ?? "");
        setScheduledAt(toLocalDatetime(found.scheduled_at));
        setSentAt(found.sent_at);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [isEdit, id]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        title,
        body,
        ...(announcementId ? { announcement_id: announcementId } : {}),
        ...(lang ? { lang } : {}),
      };
      if (scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      if (isEdit && id) {
        await api.updatePushMessage(id, payload);
      } else {
        payload.service_id = serviceId;
        await api.createPushMessage(payload);
      }
      navigate("/push");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onCancel() {
    if (!id) return;
    if (!confirm("この未送信 push をキャンセル (削除) しますか？")) return;
    try {
      await api.deletePushMessage(id);
      navigate("/push");
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const isSent = sentAt !== null;

  return (
    <section data-testid="push-form">
      <h1>{isEdit ? "push を編集" : "push を新規投稿"}</h1>
      {error && <div className="error-banner" data-testid="form-error">{error}</div>}
      {isSent && <div className="error-banner">この push は既に送信済みのため編集できません</div>}
      <form className="row" onSubmit={onSubmit}>
        <div className="split">
          <label className="field">
            <span>service_id</span>
            <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} disabled={isEdit}>
              {SERVICE_IDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="field">
            <span>lang (任意)</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="">(指定なし)</option>
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>
        <label className="field">
          <span>タイトル</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="title-input" />
        </label>
        <label className="field">
          <span>本文</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} required data-testid="body-input" style={{ minHeight: 80 }} />
        </label>
        <label className="field">
          <span>announcement_id (任意, タップ時のディープリンク)</span>
          <input type="text" value={announcementId} onChange={(e) => setAnnouncementId(e.target.value)} />
        </label>
        <label className="field">
          <span>配信時刻 (未指定で即時送信)</span>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            data-testid="scheduled-at-input"
          />
        </label>
        <div className="toolbar">
          <button type="submit" className="btn" disabled={submitting || isSent} data-testid="submit-btn">
            {submitting ? "保存中…" : isEdit ? "更新する" : "登録する"}
          </button>
          {isEdit && !isSent && (
            <button type="button" className="btn danger" onClick={onCancel}>
              キャンセル (削除)
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
